// components/Clinician/OrderList.jsx
// Order management list with high-fidelity UI matching premium design mockup.
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  FormControl,
  InputAdornment,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  IconButton,
} from "@mui/material";
import {
  Search,
  CalendarMonth,
  FilterList,
  ViewList,
  Download,
  Add,
  FileUpload,
  VisibilityOutlined,
  EditOutlined,
  Print,
  CancelOutlined,
  DescriptionOutlined,
  Close,
} from "@mui/icons-material";
import { orderService } from "../../services/ris";
import { isRisOrderCompleted, getReportStudyRef } from "../../utils/risReportUtils";
import ReportViewer from "../Common/ReportViewer";
import PatientRegistration from "./PatientRegistration";
import CreateOrder from "./CreateOrder";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { exportToCSV } from "../../utils/exportUtils";
import ColumnSelectorPopover from "../Common/ColumnSelectorPopover";

const ORDER_COLUMNS = [
  { key: 'orderNo', label: 'Order No.' },
  { key: 'accessionNo', label: 'Accession No.' },
  { key: 'patient', label: 'Patient', required: true },
  { key: 'studyDescription', label: 'Study Description' },
  { key: 'modality', label: 'Modality' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Order Status' },
  { key: 'orderedOn', label: 'Ordered On' },
  { key: 'orderingPhysician', label: 'Ordering Physician' },
  { key: 'actions', label: 'Actions', required: true },
];

const IN_PROGRESS_STATUSES = ["NEW", "ARRIVAL", "READY", "STARTED"];
const AWAITING_REPORT_STATUSES = ["ENDEXAM", "TOREPORT", "PRELIM"];
const STATUS_STYLES = {
  NEW: { label: "In Progress", color: "#9333EA", bg: "#F5F3FF" },
  ARRIVAL: { label: "In Progress", color: "#9333EA", bg: "#F5F3FF" },
  READY: { label: "In Progress", color: "#9333EA", bg: "#F5F3FF" },
  STARTED: { label: "In Progress", color: "#9333EA", bg: "#F5F3FF" },
  ENDEXAM: { label: "Awaiting Report", color: "#EA580C", bg: "#FFF7ED" },
  REPORTED: { label: "Completed", color: "#10B981", bg: "#ECFDF5" },
  CANCELLED: { label: "Cancelled", color: "#EF4444", bg: "#FEF2F2" },
  QC: { label: "On Hold", color: "#F59E0B", bg: "#FFFBEB" },
  TOREPORT: { label: "Awaiting Report", color: "#EA580C", bg: "#FFF7ED" },
  PRELIM: { label: "Awaiting Report", color: "#EA580C", bg: "#FFF7ED" },
};

const getStatusLabel = (order) => {
  if (isRisOrderCompleted(order)) return "Completed";
  return STATUS_STYLES[order.orderStatus]?.label || "In Progress";
};

export default function OrderList({ preselectedPatient = null }) {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const inputBg = isDark ? '#121212' : '#FFFFFF';
  const cardBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';
  const cardBorder = `1px solid ${cardBorderColor}`;
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#64748B';
  const textColorMuted = isDark ? '#94A3B8' : '#94A3B8';
  const hoverBgSubtle = isDark ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC';
  const tableHeaderBg = isDark ? '#18181B' : '#F8FAFC';

  const getStatusStyle = (order) => {
    if (isRisOrderCompleted(order)) {
      return {
        bg: isDark ? 'rgba(16, 185, 129, 0.18)' : '#ECFDF5',
        color: isDark ? '#34D399' : '#059669',
        label: 'Completed'
      };
    }
    const s = order.orderStatus;
    if (["ENDEXAM", "TOREPORT", "PRELIM"].includes(s)) {
      return {
        bg: isDark ? 'rgba(234, 88, 12, 0.18)' : '#FFF7ED',
        color: isDark ? '#FB923C' : '#EA580C',
        label: 'Awaiting Report'
      };
    }
    if (s === "CANCELLED") {
      return {
        bg: isDark ? 'rgba(239, 68, 68, 0.18)' : '#FEF2F2',
        color: isDark ? '#F87171' : '#EF4444',
        label: 'Cancelled'
      };
    }
    if (s === "QC") {
      return {
        bg: isDark ? 'rgba(245, 158, 11, 0.18)' : '#FFFBEB',
        color: isDark ? '#FBBF24' : '#D97706',
        label: 'On Hold'
      };
    }
    return {
      bg: isDark ? 'rgba(147, 51, 234, 0.18)' : '#F5F3FF',
      color: isDark ? '#C084FC' : '#9333EA',
      label: 'In Progress'
    };
  };

  const DetailRow = ({ label, value }) => (
    <Grid item xs={6} sm={3}>
      <Typography
        variant="caption"
        sx={{ color: textColorSecondary, fontWeight: 600, display: "block", fontSize: "0.72rem", mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: textColorPrimary, fontWeight: 500, fontSize: "0.8rem" }}>
        {value || "-"}
      </Typography>
    </Grid>
  );

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const [showRegister, setShowRegister] = useState(false);
  const [showCreateOrder, setShowCreateOrder] = useState(Boolean(preselectedPatient));
  const [selectedPatientForOrder, setSelectedPatientForOrder] = useState(preselectedPatient || null);

  useEffect(() => {
    if (preselectedPatient) {
      setSelectedPatientForOrder(preselectedPatient);
      setShowCreateOrder(true);
    }
  }, [preselectedPatient]);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [selectedOrderForReport, setSelectedOrderForReport] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Unified Search & Filters states
  const [searchVal, setSearchVal] = useState("");
  const [dateRange, setDateRange] = useState("All Time");
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [orderStatus, setOrderStatus] = useState("All");
  const [priority, setPriority] = useState("All");
  const [physician, setPhysician] = useState("All");
  const [modality, setModality] = useState("All");
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('dycovue_orders_columns');
      return saved ? JSON.parse(saved) : ORDER_COLUMNS.map(c => c.key);
    } catch (e) {
      return ORDER_COLUMNS.map(c => c.key);
    }
  });

  const handleToggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      try { localStorage.setItem('dycovue_orders_columns', JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  const handleResetColumns = () => {
    const all = ORDER_COLUMNS.map(c => c.key);
    setVisibleColumns(all);
    try { localStorage.setItem('dycovue_orders_columns', JSON.stringify(all)); } catch (e) {}
  };

  const handleExportOrders = () => {
    const exportCols = [
      { key: 'orderNo', label: 'Order No.' },
      { key: 'accessionNo', label: 'Accession No.' },
      { key: 'patientName', label: 'Patient Name' },
      { key: 'patientId', label: 'Patient ID', formatter: (v, o) => o.patientId || o.mrn || '' },
      { key: 'patientSex', label: 'Gender' },
      { key: 'patientAge', label: 'Age' },
      { key: 'procedure', label: 'Study Description' },
      { key: 'modality', label: 'Modality' },
      { key: 'priority', label: 'Priority' },
      { key: 'status', label: 'Status', formatter: (v, o) => getStatusLabel(o) },
      { key: 'orderedOnDate', label: 'Ordered Date' },
      { key: 'orderedOnTime', label: 'Ordered Time' },
      { key: 'physicianName', label: 'Ordering Physician' },
    ];
    exportToCSV(filteredList, `orders_export_${new Date().toISOString().split('T')[0]}`, exportCols);
  };

  // Realistic mock lists for dropdowns to look high-fidelity
  const PHYSICIANS = [
    "Dr. James Wilson",
    "Dr. Sana Malik",
    "Dr. Ahmed Raza",
    "Dr. Mary Thomas",
    "Dr. Linda Joseph",
  ];
  const MODALITIES = ["CT", "MRI", "XRAY", "MAMMO", "US", "ANGIO", "BMD"];

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      // Smart search criteria parsing
      if (searchVal) {
        if (
          searchVal.toLowerCase().startsWith("ord-") ||
          searchVal.toLowerCase().startsWith("dv-")
        ) {
          params.accession = searchVal;
        } else if (/^\d{4}$/.test(searchVal.trim())) {
          params.search = searchVal.trim();
        } else if (/^\d+$/.test(searchVal)) {
          params.mrn = searchVal;
        } else {
          params.patientName = searchVal;
        }
      }

      if (activeTab === 3) {
        params.status = "CANCELLED";
      } else if (orderStatus === "Cancelled") {
        params.status = "CANCELLED";
      } else if (orderStatus === "On Hold") {
        params.status = "QC";
      } else if (orderStatus === "Awaiting Report") {
        // Fetched client-side — ENDEXAM/TOREPORT/PRELIM share this label
      }

      if (priority !== "All") {
        params.priority = priority.toUpperCase();
      }

      if (modality !== "All") {
        params.modality = modality;
      }

      if (dateRange === "Today") {
        const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
        params.dateFrom = todayStart.toISOString();
        params.dateTo = todayEnd.toISOString();
      } else if (dateRange === "Last 7 Days") {
        const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7); sevenDaysAgo.setHours(0, 0, 0, 0);
        params.dateFrom = sevenDaysAgo.toISOString();
      } else if (dateRange === "This Month") {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        params.dateFrom = start.toISOString();
      } else if (dateRange === "This Year") {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear() + 1, 0, 1);
        params.dateFrom = start.toISOString();
        params.dateTo = end.toISOString();
        params.year = now.getFullYear();
      } else if (dateRange && dateRange.startsWith("Year ")) {
        const yr = parseInt(dateRange.replace("Year ", ""), 10);
        const start = new Date(yr, 0, 1);
        const end = new Date(yr + 1, 0, 1);
        params.dateFrom = start.toISOString();
        params.dateTo = end.toISOString();
        params.year = yr;
      }

      // Make API call
      const { data } = await orderService.list(params);

      // Map and adjust data fields for high fidelity display
      const list = (data.orders || []).map((o) => {
        // Enforce mock-like values if missing, keeping MRN/Accession/Status completely functional
        const orderedDate = o.orderedAt ? new Date(o.orderedAt) : new Date();
        return {
          ...o,
          orderNo: o.accessionNumber
            ? `ORD-${o.accessionNumber.split("-")[1] || o.accessionNumber}`
            : "-",
          accessionNo: o.accessionNumber || "-",
          patientName: o.patientName ? o.patientName.replace(/\^/g, " ") : "-",
          patientSex: o.patientSex || "-",
          patientAge: o.patientAge ? o.patientAge.replace("Y", "") + " Y" : "-",
          procedure: o.procedureDescription || o.procedureCode || "-",
          orderedOnDate: orderedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          orderedOnTime: orderedDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
          physicianName: o.referrerName || "-",
          priority: o.priority || "ROUTINE",
          orderStatus: o.orderStatus || "NEW",
          pacsStudyId: o.pacsStudyId,
          selfReportStatus: o.selfReportStatus,
          dycovueReportStatus: o.dycovueReportStatus,
          reportCompleted: o.reportCompleted,
        };
      });

      let filtered = list;
      if (orderStatus === "In Progress") {
        filtered = list.filter((o) =>
          IN_PROGRESS_STATUSES.includes(o.orderStatus),
        );
      } else if (orderStatus === "Completed") {
        filtered = list.filter((o) => isRisOrderCompleted(o));
      } else if (orderStatus === "Awaiting Report") {
        filtered = list.filter((o) =>
          AWAITING_REPORT_STATUSES.includes(o.orderStatus),
        );
      }

      setOrders(filtered);
      setTotal(data.total || filtered.length || 0);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    searchVal,
    orderStatus,
    priority,
    modality,
    activeTab,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleClearFilters = () => {
    setSearchVal("");
    setOrderStatus("All");
    setPriority("All");
    setPhysician("All");
    setModality("All");
    setPage(0);
  };

  const loadOrderDetails = async (orderId) => {
    const { data } = await orderService.getById(orderId);
    return data.order;
  };

  const handleViewOrder = async (order) => {
    try {
      const full = await loadOrderDetails(order._id);
      setViewOrder(full);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to load order",
        severity: "error",
      });
    }
  };

  const handleEditOrder = async (order) => {
    try {
      const full = await loadOrderDetails(order._id);
      setEditOrder(full);
      setEditForm({
        modality: full.modality || "",
        procedureDescription: full.procedureDescription || "",
        bodyPart: full.bodyPart || "",
        referrerName: full.referrerName || "",
        departmentName: full.departmentName || "",
        clinicalIndication: full.clinicalIndication || "",
        notes: full.notes || "",
        priority: full.priority || "ROUTINE",
        scheduledDate: full.scheduledDate
          ? new Date(full.scheduledDate).toISOString().slice(0, 10)
          : "",
        scheduledTime: full.scheduledTime || "",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to load order",
        severity: "error",
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!editOrder) return;
    setEditLoading(true);
    try {
      await orderService.update(editOrder._id, editForm);
      setSnackbar({
        open: true,
        message: "Order updated successfully",
        severity: "success",
      });
      setEditOrder(null);
      fetchOrders();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to update order",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!editOrder) return;
    setEditLoading(true);
    try {
      await orderService.cancel(
        editOrder._id,
        cancelReason || "Cancelled from orders page",
      );
      setSnackbar({
        open: true,
        message: "Order cancelled",
        severity: "success",
      });
      setCancelConfirmOpen(false);
      setCancelReason("");
      setEditOrder(null);
      fetchOrders();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to cancel order",
        severity: "error",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const canEditOrder = (status) =>
    ["NEW", "ARRIVAL", "READY", "STARTED"].includes(status);
  const canCancelOrder = (status) =>
    status !== "CANCELLED" && status !== "REPORTED";

  // Filter orders by active tab
  const getFilteredOrders = () => {
    if (activeTab === 0) return orders;
    return orders.filter((o) => {
      const label = getStatusLabel(o);
      if (activeTab === 1) return label === "In Progress";
      if (activeTab === 2) return label === "Completed";
      if (activeTab === 3) return o.orderStatus === "CANCELLED";
      return true;
    });
  };

  const tabCounts = {
    all: total || orders.length,
    inProgress: orders.filter((o) => getStatusLabel(o) === "In Progress").length,
    completed: orders.filter((o) => isRisOrderCompleted(o)).length,
    awaitingReport: orders.filter((o) =>
      AWAITING_REPORT_STATUSES.includes(o.orderStatus),
    ).length,
    cancelled: orders.filter((o) => o.orderStatus === "CANCELLED").length,
  };

  const actionBtnSx = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "4px",
    borderColor: cardBorderColor,
    color: textColorSecondary,
    height: "24px",
    fontSize: "0.72rem",
    px: 1,
    py: 0,
    "&:hover": { borderColor: "#00665C", color: isDark ? "#2DD4BF" : "#00665C", bgcolor: hoverBgSubtle },
  };

  const renderContextActions = (order) => {
    const label = getStatusLabel(order);
    const completed = isRisOrderCompleted(order);
    const isClaimed = Boolean(
      order.assignedRadiologist ||
      order.orderStatus === 'claimed' ||
      ['claimed', 'in_progress'].includes(order.dycovueReportStatus)
    );
    const showEdit = order.orderStatus !== "CANCELLED" && !completed && !isClaimed;

    const handleViewReport = () => {
      setSelectedOrderForReport(getReportStudyRef(order));
      setReportViewerOpen(true);
    };

    return (
      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
        {completed ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DescriptionOutlined sx={{ fontSize: 14 }} />}
            onClick={handleViewReport}
            sx={actionBtnSx}
          >
            Report
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
            onClick={() => handleViewOrder(order)}
            sx={actionBtnSx}
          >
            View
          </Button>
        )}

        {showEdit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
            onClick={() => handleEditOrder(order)}
            sx={actionBtnSx}
          >
            Edit
          </Button>
        )}

        {label === "Completed" && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Print sx={{ fontSize: 14 }} />}
            onClick={() => window.print()}
            sx={actionBtnSx}
          >
            Print
          </Button>
        )}
      </Box>
    );
  };

  const filteredList = getFilteredOrders();

  return (
    <Box sx={{ p: 0.5 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: textColorPrimary,
              fontSize: "1.05rem",
              lineHeight: 1.2,
              mb: 0.25,
            }}
          >
            Orders
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: textColorSecondary, fontSize: "0.78rem" }}
          >
            Manage and track all imaging orders.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileUpload sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textColorPrimary,
              borderColor: cardBorderColor,
              borderRadius: "6px",
              bgcolor: cardBg,
              height: "32px",
              px: 1.5,
              fontSize: "0.8rem",
              "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1", bgcolor: hoverBgSubtle },
            }}
          >
            Import Order
          </Button>
          <Button
            variant="contained"
            startIcon={<Add sx={{ fontSize: 16 }} />}
            onClick={() => setShowCreateOrder(true)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#00665C",
              color: "#FFFFFF",
              borderRadius: "6px",
              height: "32px",
              px: 1.75,
              fontSize: "0.8rem",
              boxShadow: "none",
              "&:hover": { bgcolor: "#004D47", boxShadow: "none" },
            }}
          >
            New Order
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper
        sx={{
          p: 1.5,
          mb: 1.5,
          border: cardBorder,
          boxShadow: "none",
          borderRadius: "10px",
          bgcolor: cardBg,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 1.25,
            alignItems: "center",
            width: "100%",
            overflowX: "auto",
            py: 0.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: "170px",
              flex: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Search
            </Typography>
            <TextField
              placeholder="Patient, Order #, Accession #..."
              size="small"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  bgcolor: inputBg,
                  height: "30px",
                  fontSize: "0.78rem",
                  color: textColorPrimary,
                  "& fieldset": { borderColor: cardBorderColor },
                  "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1" },
                  "&.Mui-focused fieldset": { borderColor: "#0D9488" },
                },
                "& .MuiInputBase-input": {
                  py: 0,
                  px: 1,
                  fontSize: "0.78rem",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search fontSize="small" sx={{ color: textColorMuted, fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: "140px",
              flex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Date Range
            </Typography>
            <Button
              onClick={(e) => setDateAnchorEl(e.currentTarget)}
              startIcon={<CalendarMonth fontSize="small" sx={{ color: textColorMuted, fontSize: 15 }} />}
              sx={{
                borderRadius: "6px",
                border: cardBorder,
                textTransform: "none",
                color: textColorPrimary,
                px: 1.25,
                height: "30px",
                fontSize: "0.78rem",
                fontWeight: 500,
                bgcolor: inputBg,
                justifyContent: "flex-start",
                "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1", bgcolor: hoverBgSubtle },
              }}
            >
              {dateRange}
            </Button>
            <Popover
              open={Boolean(dateAnchorEl)}
              anchorEl={dateAnchorEl}
              onClose={() => setDateAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              PaperProps={{
                sx: {
                  p: 1,
                  bgcolor: cardBg,
                  borderRadius: "8px",
                  border: cardBorder,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: textColorPrimary, mb: 0.75, px: 0.5 }}>
                Select Date Range
              </Typography>
              {[
                { label: "Today", value: "Today" },
                { label: "Last 7 Days", value: "Last 7 Days" },
                {
                  label: (() => {
                    const now = new Date();
                    const year = now.getFullYear();
                    const month = now.getMonth();
                    const start = new Date(year, month, 1);
                    const end = new Date(year, month + 1, 0);
                    const fmt = (d) =>
                      `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                    return `This Month (${fmt(start)} - ${fmt(end)})`;
                  })(),
                  value: "This Month",
                },
                {
                  label: `This Year (${new Date().getFullYear()})`,
                  value: "This Year",
                },
                {
                  label: `Year ${new Date().getFullYear() - 1}`,
                  value: `Year ${new Date().getFullYear() - 1}`,
                },
                {
                  label: `Year ${new Date().getFullYear() - 2}`,
                  value: `Year ${new Date().getFullYear() - 2}`,
                },
                { label: "All Time", value: "All Time" },
              ].map((opt) => (
                <MenuItem
                  key={opt.value}
                  onClick={() => {
                    setDateRange(opt.value);
                    setDateAnchorEl(null);
                  }}
                  sx={{
                    fontSize: "0.78rem",
                    py: 0.5,
                    borderRadius: "4px",
                    fontWeight: dateRange === opt.value ? 700 : 400,
                    bgcolor: dateRange === opt.value ? (isDark ? "rgba(13,148,136,0.15)" : "#EFF6FF") : "transparent",
                    color: textColorPrimary,
                  }}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </Popover>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: "105px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Order Status
            </Typography>
            <FormControl size="small">
              <Select
                value={orderStatus}
                onChange={(e) => setOrderStatus(e.target.value)}
                sx={{
                  borderRadius: "6px",
                  bgcolor: inputBg,
                  height: "30px",
                  fontSize: "0.78rem",
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                <MenuItem value="In Progress" sx={{ fontSize: "0.78rem" }}>In Progress</MenuItem>
                <MenuItem value="Completed" sx={{ fontSize: "0.78rem" }}>Completed</MenuItem>
                <MenuItem value="Cancelled" sx={{ fontSize: "0.78rem" }}>Cancelled</MenuItem>
                <MenuItem value="On Hold" sx={{ fontSize: "0.78rem" }}>On Hold</MenuItem>
                <MenuItem value="Awaiting Report" sx={{ fontSize: "0.78rem" }}>Awaiting Report</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: "95px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Priority
            </Typography>
            <FormControl size="small">
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                sx={{
                  borderRadius: "6px",
                  bgcolor: inputBg,
                  height: "30px",
                  fontSize: "0.78rem",
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                <MenuItem value="Routine" sx={{ fontSize: "0.78rem" }}>Routine</MenuItem>
                <MenuItem value="Stat" sx={{ fontSize: "0.78rem" }}>Stat</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: "135px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Ordering Physician
            </Typography>
            <FormControl size="small">
              <Select
                value={physician}
                onChange={(e) => setPhysician(e.target.value)}
                sx={{
                  borderRadius: "6px",
                  bgcolor: inputBg,
                  height: "30px",
                  fontSize: "0.78rem",
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                {PHYSICIANS.map((p) => (
                  <MenuItem key={p} value={p} sx={{ fontSize: "0.78rem" }}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              minWidth: "90px",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem" }}
            >
              Modality
            </Typography>
            <FormControl size="small">
              <Select
                value={modality}
                onChange={(e) => setModality(e.target.value)}
                sx={{
                  borderRadius: "6px",
                  bgcolor: inputBg,
                  height: "30px",
                  fontSize: "0.78rem",
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value="All" sx={{ fontSize: "0.78rem" }}>All</MenuItem>
                {MODALITIES.map((m) => (
                  <MenuItem key={m} value={m} sx={{ fontSize: "0.78rem" }}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 1, pt: 2, shrink: 0 }}>
            <Button
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                color: textColorSecondary,
                fontWeight: 600,
                fontSize: "0.78rem",
                minWidth: "50px",
                height: "30px",
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              startIcon={<FilterList sx={{ fontSize: 16 }} />}
              onClick={fetchOrders}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#00665C",
                borderRadius: "6px",
                color: "#FFFFFF",
                fontSize: "0.78rem",
                height: "30px",
                minWidth: "80px",
                boxShadow: "none",
                "&:hover": { bgcolor: "#004D47", boxShadow: "none" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs & Table Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            minHeight: "32px",
            "& .MuiTabs-indicator": {
              backgroundColor: "#00665C",
              height: "2.5px",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              color: textColorSecondary,
              minWidth: "auto",
              minHeight: "32px",
              py: 0.5,
              px: 1.5,
              "&.Mui-selected": { color: isDark ? "#2DD4BF" : "#00665C" },
            },
          }}
        >
          <Tab label={`All Orders (${tabCounts.all})`} />
          <Tab label={`In Progress (${tabCounts.inProgress})`} />
          <Tab label={`Completed (${tabCounts.completed})`} />
          <Tab label={`Cancelled (${tabCounts.cancelled})`} />
        </Tabs>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ViewList sx={{ fontSize: 15 }} />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textColorPrimary,
              borderColor: cardBorderColor,
              borderRadius: "6px",
              bgcolor: cardBg,
              height: "30px",
              fontSize: "0.75rem",
              px: 1.25,
              "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1", bgcolor: hoverBgSubtle },
            }}
          >
            Columns
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download sx={{ fontSize: 15 }} />}
            onClick={handleExportOrders}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: textColorPrimary,
              borderColor: cardBorderColor,
              borderRadius: "6px",
              bgcolor: cardBg,
              height: "30px",
              fontSize: "0.75rem",
              px: 1.25,
              "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1", bgcolor: hoverBgSubtle },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Orders Table */}
      <TableContainer
        component={Paper}
        sx={{
          border: cardBorder,
          boxShadow: "none",
          borderRadius: "10px",
          overflowX: "auto",
          bgcolor: cardBg,
        }}
      >
        <Table size="small" sx={{ minWidth: "1200px" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: tableHeaderBg }}>
              {visibleColumns.includes('orderNo') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Order No.
                </TableCell>
              )}
              {visibleColumns.includes('accessionNo') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Accession No.
                </TableCell>
              )}
              {visibleColumns.includes('patient') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Patient
                </TableCell>
              )}
              {visibleColumns.includes('studyDescription') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Study Description
                </TableCell>
              )}
              {visibleColumns.includes('modality') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Modality
                </TableCell>
              )}
              {visibleColumns.includes('priority') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Priority
                </TableCell>
              )}
              {visibleColumns.includes('status') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Order Status
                </TableCell>
              )}
              {visibleColumns.includes('orderedOn') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Ordered On
                </TableCell>
              )}
              {visibleColumns.includes('orderingPhysician') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, borderColor: cardBorderColor }}>
                  Ordering Physician
                </TableCell>
              )}
              {visibleColumns.includes('actions') && (
                <TableCell sx={{ fontWeight: 700, color: textColorSecondary, fontSize: "0.72rem", py: "6px !important", px: 1.25, textAlign: "right", borderColor: cardBorderColor }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length || 10}
                  sx={{ textAlign: "center", py: 4, borderColor: cardBorderColor }}
                >
                  <CircularProgress size={24} sx={{ color: "#00665C" }} />
                </TableCell>
              </TableRow>
            ) : filteredList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length || 10}
                  sx={{
                    textAlign: "center",
                    py: 4,
                    borderColor: cardBorderColor,
                    color: textColorSecondary,
                    fontSize: "0.8rem",
                  }}
                >
                  No orders found
                </TableCell>
              </TableRow>
            ) : (
              filteredList.map((order, index) => {
                const style = getStatusStyle(order);
                const isHighPriority =
                  order.priority === "STAT" ||
                  order.priority === "URGENT" ||
                  order.priority === "HIGH";

                return (
                  <TableRow key={order._id || index} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    {visibleColumns.includes('orderNo') && (
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          color: textColorSecondary,
                          fontWeight: 500,
                          py: "4px !important",
                          px: 1.25,
                          borderColor: cardBorderColor,
                        }}
                      >
                        {order.orderNo}
                      </TableCell>
                    )}
                    {visibleColumns.includes('accessionNo') && (
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          color: isDark ? "#60A5FA" : "#2563EB",
                          fontWeight: 700,
                          py: "4px !important",
                          px: 1.25,
                          borderColor: cardBorderColor,
                        }}
                      >
                        {order.accessionNo}
                      </TableCell>
                    )}
                    {visibleColumns.includes('patient') && (
                      <TableCell sx={{ py: "4px !important", px: 1.25, borderColor: cardBorderColor }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 700,
                              color: textColorPrimary,
                            }}
                          >
                            {order.patientName}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                          >
                            {order.patientSex}, {order.patientAge}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.includes('studyDescription') && (
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          color: textColorSecondary,
                          fontWeight: 500,
                          py: "4px !important",
                          px: 1.25,
                          borderColor: cardBorderColor,
                        }}
                      >
                        {order.procedure}
                      </TableCell>
                    )}
                    {visibleColumns.includes('modality') && (
                      <TableCell sx={{ py: "4px !important", px: 1.25, borderColor: cardBorderColor }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 0.8,
                            py: 0.15,
                            borderRadius: "4px",
                            border: cardBorder,
                            bgcolor: hoverBgSubtle,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              color: textColorPrimary,
                            }}
                          >
                            {order.modality}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.includes('priority') && (
                      <TableCell sx={{ py: "4px !important", px: 1.25, borderColor: cardBorderColor }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            px: 0.85,
                            py: 0.15,
                            borderRadius: "4px",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: isHighPriority
                              ? (isDark ? "rgba(239,68,68,0.2)" : "#FEF2F2")
                              : (isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9"),
                            color: isHighPriority
                              ? (isDark ? "#F87171" : "#EF4444")
                              : textColorSecondary,
                          }}
                        >
                          {isHighPriority ? "High" : "Routine"}
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.includes('status') && (
                      <TableCell sx={{ py: "4px !important", px: 1.25, borderColor: cardBorderColor }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            px: 1,
                            py: 0.15,
                            borderRadius: "10px",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: style.bg,
                            color: style.color,
                          }}
                        >
                          {getStatusLabel(order)}
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.includes('orderedOn') && (
                      <TableCell sx={{ py: "4px !important", px: 1.25, borderColor: cardBorderColor }}>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: textColorPrimary,
                              fontWeight: 500,
                            }}
                          >
                            {order.orderedOnDate}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.68rem", color: textColorSecondary }}
                          >
                            {order.orderedOnTime}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {visibleColumns.includes('orderingPhysician') && (
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          color: textColorSecondary,
                          fontWeight: 500,
                          py: "4px !important",
                          px: 1.25,
                          borderColor: cardBorderColor,
                        }}
                      >
                        {order.physicianName}
                      </TableCell>
                    )}
                    {visibleColumns.includes('actions') && (
                      <TableCell
                        sx={{ textAlign: "right", py: "4px !important", px: 1.25, borderColor: cardBorderColor }}
                      >
                        {renderContextActions(order)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Dynamic Pagination Footer */}
        <Box
          sx={{
            py: 1,
            px: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            borderTop: cardBorder,
            bgcolor: cardBg,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: textColorSecondary, fontWeight: 500, fontSize: "0.72rem" }}
          >
            Showing {filteredList.length > 0 ? page * rowsPerPage + 1 : 0} to{" "}
            {Math.min((page + 1) * rowsPerPage, tabCounts.all)} of{" "}
            {tabCounts.all} orders
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.72rem" }}
              >
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                size="small"
                sx={{
                  borderRadius: "6px",
                  height: "26px",
                  fontSize: "0.72rem",
                  bgcolor: inputBg,
                  color: textColorPrimary,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: cardBorderColor,
                  },
                }}
              >
                <MenuItem value={10} sx={{ fontSize: "0.72rem" }}>10</MenuItem>
                <MenuItem value={25} sx={{ fontSize: "0.72rem" }}>25</MenuItem>
                <MenuItem value={50} sx={{ fontSize: "0.72rem" }}>50</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
      </TableContainer>

      {/* Functional Dialogs */}
      <PatientRegistration
        open={showRegister}
        onClose={() => setShowRegister(false)}
        onSuccess={() => {
          setShowRegister(false);
          setShowCreateOrder(true);
        }}
        onCreateOrder={(patient) => {
          setShowRegister(false);
          setSelectedPatientForOrder(patient);
          setShowCreateOrder(true);
        }}
      />
      <CreateOrder
        open={showCreateOrder}
        preselectedPatient={selectedPatientForOrder}
        onClearPatient={() => setSelectedPatientForOrder(null)}
        onClose={() => {
          setShowCreateOrder(false);
          setSelectedPatientForOrder(null);
        }}
        onSuccess={() => {
          setShowCreateOrder(false);
          setSelectedPatientForOrder(null);
          fetchOrders();
        }}
      />

      {/* View Order Modal */}
      <Dialog
        open={Boolean(viewOrder)}
        onClose={() => setViewOrder(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "90vw", md: "840px" },
            maxWidth: "860px !important",
            borderRadius: "10px",
            bgcolor: cardBg,
            border: cardBorder,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: textColorPrimary, py: 1.5, px: 2, fontSize: "1rem", borderBottom: cardBorder }}>
          Order Details
          {viewOrder?.accessionNumber && (
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1, color: textColorSecondary, fontSize: "0.85rem" }}
            >
              ({viewOrder.accessionNumber})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: cardBorderColor, p: 2 }}>
          {viewOrder && (
            <Grid container spacing={1.5}>
              <DetailRow
                label="Accession No."
                value={viewOrder.accessionNumber}
              />
              <DetailRow label="Request No." value={viewOrder.requestNumber} />
              <DetailRow
                label="Patient"
                value={viewOrder.patientName?.replace(/\^/g, " ")}
              />
              <DetailRow label="MRN" value={viewOrder.patientMrn} />
              <DetailRow label="Modality" value={viewOrder.modality} />
              <DetailRow
                label="Study Description"
                value={viewOrder.procedureDescription}
              />
              <DetailRow label="Body Part" value={viewOrder.bodyPart} />
              <DetailRow label="Priority" value={viewOrder.priority} />
              <DetailRow
                label="Status"
                value={getStatusLabel(viewOrder)}
              />
              <DetailRow
                label="Ordering Physician"
                value={viewOrder.referrerName}
              />
              <DetailRow label="Department" value={viewOrder.departmentName} />
              <DetailRow
                label="Scheduled Date"
                value={
                  viewOrder.scheduledDate
                    ? new Date(viewOrder.scheduledDate).toLocaleDateString()
                    : "-"
                }
              />
              <DetailRow
                label="Scheduled Time"
                value={viewOrder.scheduledTime}
              />
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.72rem", display: "block", mb: 0.25 }}
                >
                  Clinical Indication
                </Typography>
                <Typography variant="body2" sx={{ color: textColorPrimary, fontSize: "0.8rem" }}>
                  {viewOrder.clinicalIndication || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  sx={{ color: textColorSecondary, fontWeight: 600, fontSize: "0.72rem", display: "block", mb: 0.25 }}
                >
                  Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: textColorPrimary, whiteSpace: "pre-wrap", fontSize: "0.8rem" }}
                >
                  {viewOrder.notes || "-"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder }}>
          <Button
            onClick={() => setViewOrder(null)}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", height: "30px", color: textColorPrimary }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Order Modal */}
      <Dialog
        open={Boolean(editOrder)}
        onClose={() => setEditOrder(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "90vw", md: "860px" },
            maxWidth: "880px !important",
            borderRadius: "10px",
            bgcolor: cardBg,
            border: cardBorder,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: textColorPrimary, py: 1.5, px: 2, fontSize: "1rem", borderBottom: cardBorder }}>
          Edit Order
          {editOrder?.accessionNumber && (
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 1, color: textColorSecondary, fontSize: "0.85rem" }}
            >
              ({editOrder.accessionNumber})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: cardBorderColor, p: 2 }}>
          {editOrder && (
            <Box component="form" sx={{ pt: 0.5 }}>
              {!canEditOrder(editOrder.orderStatus) && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: "0.8rem" }}>
                  This order can only be viewed. Editing is disabled for its current status.
                </Alert>
              )}
              <Grid container spacing={1.5}>
                {/* Row 1: Modality, Priority, Scheduled Date, Body Part */}
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Modality"
                    size="small"
                    fullWidth
                    value={editForm.modality || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, modality: e.target.value }))
                    }
                    InputProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    label="Priority"
                    size="small"
                    fullWidth
                    value={editForm.priority || "ROUTINE"}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, priority: e.target.value }))
                    }
                    SelectProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  >
                    <MenuItem value="ROUTINE" sx={{ fontSize: "0.8rem" }}>Routine</MenuItem>
                    <MenuItem value="STAT" sx={{ fontSize: "0.8rem" }}>STAT</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Scheduled Date"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true, sx: { fontSize: "0.8rem" } }}
                    value={editForm.scheduledDate || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        scheduledDate: e.target.value,
                      }))
                    }
                    InputProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Body Part"
                    size="small"
                    fullWidth
                    value={editForm.bodyPart || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, bodyPart: e.target.value }))
                    }
                    InputProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>

                {/* Row 2: Study Description, Ordering Physician, Department */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Study Description"
                    size="small"
                    fullWidth
                    value={editForm.procedureDescription || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        procedureDescription: e.target.value,
                      }))
                    }
                    InputProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Ordering Physician"
                    size="small"
                    fullWidth
                    value={editForm.referrerName || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        referrerName: e.target.value,
                      }))
                    }
                    InputProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Department"
                    size="small"
                    fullWidth
                    value={editForm.departmentName || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        departmentName: e.target.value,
                      }))
                    }
                    InputProps={{
                      sx: { height: 32, fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>

                {/* Row 3: Clinical Indication, Notes */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Clinical Indication"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={editForm.clinicalIndication || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        clinicalIndication: e.target.value,
                      }))
                    }
                    InputProps={{
                      sx: { fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Notes"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={editForm.notes || ""}
                    disabled={!canEditOrder(editOrder.orderStatus)}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, notes: e.target.value }))
                    }
                    InputProps={{
                      sx: { fontSize: "0.8rem", bgcolor: inputBg },
                    }}
                    InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, justifyContent: "space-between", borderTop: cardBorder }}>
          <Box>
            {editOrder && canCancelOrder(editOrder.orderStatus) && (
              <Button
                color="error"
                startIcon={<CancelOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setCancelConfirmOpen(true)}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", height: "30px" }}
              >
                Cancel Order
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => setEditOrder(null)}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", height: "30px", color: textColorPrimary }}
            >
              Close
            </Button>
            {editOrder && canEditOrder(editOrder.orderStatus) && (
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={editLoading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#00665C",
                  color: "#FFFFFF",
                  fontSize: "0.8rem",
                  height: "30px",
                  borderRadius: "6px",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#004D47", boxShadow: "none" },
                }}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "440px" },
            maxWidth: "460px !important",
            borderRadius: "10px",
            bgcolor: cardBg,
            border: cardBorder,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: textColorPrimary, py: 1.5, px: 2, fontSize: "1rem" }}>Cancel Order?</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: textColorSecondary, mb: 1.5, fontSize: "0.8rem" }}>
            This order will move to the Cancelled tab.
          </Typography>
          <TextField
            label="Reason (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            InputProps={{
              sx: { fontSize: "0.8rem", bgcolor: inputBg },
            }}
            InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder }}>
          <Button
            onClick={() => setCancelConfirmOpen(false)}
            sx={{ textTransform: "none", fontSize: "0.8rem", height: "30px", color: textColorPrimary }}
          >
            Keep Order
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelOrder}
            disabled={editLoading}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem", height: "30px", borderRadius: "6px", boxShadow: "none" }}
          >
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportViewerOpen}
        onClose={() => {
          setReportViewerOpen(false);
          setSelectedOrderForReport(null);
        }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '92vw', md: '860px', lg: '920px' },
            maxWidth: '940px !important',
            maxHeight: '88vh',
            borderRadius: '12px',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ px: 2, py: 1.25 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              Study Report
            </Typography>
            <IconButton onClick={() => {
              setReportViewerOpen(false);
              setSelectedOrderForReport(null);
            }} size="small">
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, overflowY: 'auto' }}>
          {selectedOrderForReport && (
            <ReportViewer
              studyId={selectedOrderForReport._id}
              study={selectedOrderForReport}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ColumnSelectorPopover
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
        columns={ORDER_COLUMNS}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        onResetColumns={handleResetColumns}
      />
    </Box>
  );
}
